import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ReplayIcon from "@mui/icons-material/Replay";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import SMQMainContainer from "../../../components/SMQMainContainer";
import useSmqPermissions from "../../../hooks/useSmqPermissions";
import SuiviService from "../../../services/smq/SuiviService";
import Message from "../../tools/Message";
import HeaderRegistre from "../registre/HeaderRegistre";

const STATUS_CONFIG = {
  done: { color: "#107844", label: "Clôturée" },
  in_progress: { color: "#9a5b00", label: "En cours" },
  open: { color: "#175b9c", label: "Ouverte" },
  reopen: { color: "#b42318", label: "NC réouverte — action insuffisante" },
};

const STEP_CONFIG = {
  1: { background: "#dff7fa", color: "#0094a5" },
  2: { background: "#eaf4ff", color: "#176bc0" },
  3: { background: "#fff2dc", color: "#e45f00" },
  4: { background: "#f2ecff", color: "#6d2de2" },
  5: { background: "#e8f9ef", color: "#159447" },
};

const SUIVI_STATE_CONFIG = {
  confirmed: {
    backgroundColor: "#dcf7e8",
    color: "#107844",
    label: "NC clôturée — efficacité confirmée",
  },
  draft: {
    backgroundColor: "#e8eef5",
    color: "#536b91",
    label: "En attente de vérification",
  },
  rejected: {
    backgroundColor: "#fde7e7",
    color: "#b42318",
    label: "NC rouverte — action insuffisante",
  },
};

const todayValue = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const displayValue = (value) => String(value ?? "").trim() || "—";

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr");

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("fr-FR");
};

const durationDays = (start, end) => {
  if (!start || !end) return "—";
  const startDate = new Date(`${String(start).slice(0, 10)}T00:00:00`);
  const endDate = new Date(`${String(end).slice(0, 10)}T00:00:00`);
  const days = Math.round((endDate.getTime() - startDate.getTime()) / 86400000);
  return Number.isFinite(days) && days >= 0 ? `${days}j` : "—";
};

const candidateLabel = (candidate) => {
  const reference = candidate.parent_id
    ? `${candidate.parent_reference || "NC parente"} → ${candidate.reference}`
    : candidate.reference;

  return `[${reference}] ${candidate.referentiel_code || candidate.processus}@${candidate.direction_code || "—"} — ${displayValue(candidate.description_constat)}`;
};

const emptyForm = (indicator = "") => ({
  commentaire: "",
  date_verification: todayValue(),
  indicateur_efficacite: indicator,
  resultat_verification: "",
  state: "draft",
  valeur_indicateur: "",
});

const formFromSuivi = (suivi, fallbackIndicator = "") => ({
  commentaire: suivi?.commentaire || "",
  date_verification: suivi?.date_verification || todayValue(),
  indicateur_efficacite: suivi?.indicateur_efficacite || fallbackIndicator,
  resultat_verification: suivi?.resultat_verification || "",
  state: suivi?.state || "draft",
  valeur_indicateur: suivi?.valeur_indicateur || "",
});

const StageCard = ({ children, number, title }) => {
  const config = STEP_CONFIG[number];
  return (
    <Paper
      elevation={0}
      sx={{ border: `1px solid ${config.color}22`, borderRadius: "10px", mb: 1, overflow: "hidden" }}
      variant="outlined"
    >
      <Box sx={{ alignItems: "center", backgroundColor: config.background, display: "flex", gap: 1, px: 1.5, py: 1 }}>
        <Box sx={{ alignItems: "center", backgroundColor: config.color, borderRadius: "50%", color: "#fff", display: "flex", fontSize: 12, fontWeight: 800, height: 24, justifyContent: "center", width: 24 }}>
          {number}
        </Box>
        <Typography color={config.color} fontSize={12} fontWeight={800}>{title}</Typography>
      </Box>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Paper>
  );
};

StageCard.propTypes = {
  children: PropTypes.node.isRequired,
  number: PropTypes.oneOf([1, 2, 3, 4, 5]).isRequired,
  title: PropTypes.string.isRequired,
};

const DetailField = ({ label, value, xs = 6 }) => (
  <Grid item xs={12} md={xs}>
    <Typography color="#536b91" fontSize={9} fontWeight={800} letterSpacing="0.06em" textTransform="uppercase">
      {label}
    </Typography>
    <Typography color="#10263d" fontSize={12} sx={{ mt: 0.35, whiteSpace: "pre-wrap" }}>
      {displayValue(value)}
    </Typography>
  </Grid>
);

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  xs: PropTypes.number,
};

const RecapSteps = ({ detail }) => {
  const triggerLabel = `[${detail.trigger_code || "—"}] ${detail.trigger_name || ""}`.trim();
  const exigenceLabel = `[${detail.exigence_code || "—"}] ${detail.exigence_name || ""}`.trim();
  const criticiteLabel = `${displayValue(detail.criticite)} (G=${displayValue(detail.gravite)} · F=${displayValue(detail.frequence)})`;
  const actions = Array.isArray(detail.actions_correctives) ? detail.actions_correctives : [];

  return (
    <>
      <StageCard number={1} title="Constat d’écart">
        <Grid container spacing={1.5}>
          <DetailField label="Référence NC" value={detail.reference} />
          <DetailField label="Date du constat" value={formatDate(detail.constat_date)} />
          <DetailField label="Type de déclencheur" value={triggerLabel} />
          <DetailField label="Constaté par" value={detail.constat_user_name} />
          <DetailField label="Direction / Service" value={`${displayValue(detail.direction_code)} — ${displayValue(detail.direction_name)}`} />
          <DetailField label="Processus concerné" value={detail.processus} />
          <DetailField label="Description du constat" value={detail.description_constat} xs={12} />
        </Grid>
      </StageCard>

      <StageCard number={2} title="Ouverture de la Fiche NC">
        <Grid container spacing={1.5}>
          <DetailField label="Exigence non respectée" value={detail.description_exigence} xs={12} />
          <DetailField label="Pilote responsable" value={detail.pilote_name} />
          <DetailField label="Délai de traitement" value={formatDate(detail.deadline)} />
          <DetailField label="Criticité (matrice)" value={criticiteLabel} />
          <DetailField label="Type d’exigence" value={exigenceLabel} />
        </Grid>
      </StageCard>

      <StageCard number={3} title="Action curative immédiate">
        <Grid container spacing={1.5}>
          <DetailField label="Description de l’action curative" value={detail.curatif?.description} xs={12} />
          <DetailField label="Responsable" value={detail.curatif?.responsable_name} />
          <DetailField label="Date de réalisation" value={formatDate(detail.curatif?.date_realisation)} />
          <DetailField label="Statut" value={detail.curatif?.statut} xs={12} />
        </Grid>
      </StageCard>

      <StageCard number={4} title="Analyse des causes">
        <Typography color="#536b91" fontSize={9} fontWeight={800}>☑ CAUSE RACINE IDENTIFIÉE</Typography>
        <Box sx={{ backgroundColor: "#eafaf2", border: "1px solid #56d6a3", borderRadius: "7px", color: "#10263d", fontSize: 12, mt: 0.6, p: 1.2 }}>
          {displayValue(detail.analyse?.cause_racine)}
        </Box>
      </StageCard>

      <StageCard number={5} title="Actions correctives sélectionnées">
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead><TableRow sx={{ "& th": { backgroundColor: "#173d61", color: "#fff", fontSize: 10, fontWeight: 800 } }}>
              <TableCell>Action corrective</TableCell><TableCell>Plan d’action</TableCell><TableCell>Indicateur d’efficacité</TableCell><TableCell>Avanc.%</TableCell><TableCell>Début</TableCell><TableCell>Fin prévue</TableCell><TableCell>Hist. date fin</TableCell><TableCell>Délai</TableCell><TableCell>Responsable</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {actions.map((action) => (
                <TableRow key={action.plan_action_id} sx={{ "& td": { borderColor: "#d5e0eb", fontSize: 11, verticalAlign: "top" } }}>
                  <TableCell>{displayValue(action.action_corrective)}</TableCell><TableCell>{displayValue(action.plan_action)}</TableCell><TableCell>{displayValue(action.indicateur_efficacite)}</TableCell><TableCell>{action.avancement}%</TableCell><TableCell>{formatDate(action.date_debut)}</TableCell><TableCell>{formatDate(action.date_fin)}</TableCell><TableCell>{formatDate(action.historique_date_fin)}</TableCell><TableCell>{durationDays(action.date_debut, action.date_fin)}</TableCell><TableCell>{displayValue(action.responsable_name)}</TableCell>
                </TableRow>
              ))}
              {actions.length === 0 && (
                <TableRow><TableCell align="center" colSpan={9}>Aucune action corrective renseignée.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StageCard>
    </>
  );
};

RecapSteps.propTypes = {
  detail: PropTypes.object.isRequired,
};

const ReadOnlySuiviHistory = ({ suivis }) => {
  const history = Array.isArray(suivis) ? suivis : [];

  return (
    <Box sx={{ borderTop: "2px dashed #c7d6e5", mt: 2.5, pt: 2.2 }}>
      <Box
        sx={{
          backgroundColor: "#10263d",
          borderRadius: "5px",
          color: "#fff",
          display: "inline-block",
          fontSize: 10,
          fontWeight: 800,
          mb: 1.5,
          px: 1.2,
          py: 0.55,
        }}
      >
        ÉTAPE 6 — HISTORIQUE DU SUIVI D’EFFICACITÉ
      </Box>

      {history.length === 0 && (
        <Alert severity="info">
          Aucun suivi d’efficacité enregistré.
        </Alert>
      )}

      {history.map((suivi, index) => {
        const stateConfig = SUIVI_STATE_CONFIG[suivi.state] || {
          backgroundColor: "#eef2f6",
          color: "#536b91",
          label: displayValue(suivi.state),
        };

        return (
          <Paper
            key={suivi.suivi_id || `${suivi.created_at || "suivi"}-${index}`}
            elevation={0}
            sx={{
              border: "1px solid #d2ddea",
              borderRadius: "8px",
              mb: 1.2,
              overflow: "hidden",
            }}
            variant="outlined"
          >
            <Box
              sx={{
                alignItems: { xs: "flex-start", sm: "center" },
                backgroundColor: "#f5f8fb",
                borderBottom: "1px solid #d2ddea",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 0.8,
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
              }}
            >
              <Typography color="#10263d" fontSize={11} fontWeight={800}>
                Suivi #{index + 1}{index === 0 ? " — le plus récent" : ""}
              </Typography>
              <Box
                sx={{
                  backgroundColor: stateConfig.backgroundColor,
                  borderRadius: "999px",
                  color: stateConfig.color,
                  fontSize: 10,
                  fontWeight: 800,
                  px: 1.2,
                  py: 0.5,
                }}
              >
                {stateConfig.label}
              </Box>
            </Box>

            <Grid container spacing={1.5} sx={{ p: 1.5 }}>
              <DetailField label="Date de vérification" value={formatDate(suivi.date_verification)} />
              <DetailField label="Vérifié par" value={suivi.evaluateur_name} />
              <DetailField label="Indicateur d’efficacité" value={suivi.indicateur_efficacite} xs={12} />
              <DetailField label="Valeur de l’indicateur" value={suivi.valeur_indicateur} />
              <DetailField label="Décision" value={suivi.decision} />
              <DetailField label="Résultat de la vérification" value={suivi.resultat_verification} xs={12} />
              <DetailField label="Commentaire" value={suivi.commentaire} xs={12} />
              <DetailField label="Statut du suivi" value={stateConfig.label} xs={12} />
            </Grid>
          </Paper>
        );
      })}
    </Box>
  );
};

ReadOnlySuiviHistory.propTypes = {
  suivis: PropTypes.arrayOf(PropTypes.object),
};

ReadOnlySuiviHistory.defaultProps = {
  suivis: [],
};

const textareaSx = (error = false) => ({
  backgroundColor: "#fff",
  border: `1px solid ${error ? "#d32f2f" : "#c7d6e5"}`,
  borderRadius: "7px",
  boxSizing: "border-box",
  color: "#10263d",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 13,
  minHeight: 58,
  outline: "none",
  padding: "10px",
  resize: "vertical",
  width: "100%",
  "&:focus": { borderColor: error ? "#d32f2f" : "#00a6b6", boxShadow: `0 0 0 2px ${error ? "rgba(211,47,47,.12)" : "rgba(0,166,182,.12)"}` },
  "&:disabled": { backgroundColor: "#edf2f7", color: "#60758a", cursor: "not-allowed" },
});

const closureStatusButtonSx = (selected) => ({
  backgroundColor: selected ? "#08a7b5" : "#fff",
  borderColor: selected ? "#08a7b5" : "#c9d6ec",
  borderRadius: "999px",
  color: selected ? "#fff" : "#18263a",
  fontSize: 11,
  fontWeight: 800,
  lineHeight: 1,
  minHeight: 29,
  minWidth: 0,
  px: 1.6,
  py: 0.7,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: selected ? "#0795a2" : "#f5f9fd",
    borderColor: selected ? "#0795a2" : "#9fb4d2",
  },
  "& .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: 0.65,
    "& svg": { fontSize: 14 },
  },
  "&.Mui-disabled": {
    backgroundColor: selected ? "rgba(8, 167, 181, 0.55)" : "#f5f7fa",
    borderColor: selected ? "rgba(8, 167, 181, 0.35)" : "#d9e1ec",
    color: selected ? "rgba(255,255,255,0.9)" : "#8796a8",
  },
});

const filterFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "6px",
    fontSize: 12,
    height: 40,
  },
  "& .MuiInputLabel-root": { fontSize: 12 },
};

const filterButtonSx = {
  borderColor: "#9fb1c3",
  borderRadius: "6px",
  color: "#173d61",
  fontSize: 11,
  fontWeight: 700,
  height: 38,
  minWidth: 160,
  textTransform: "none",
  width: { xs: "100%", sm: 160 },
  "&:hover": {
    backgroundColor: "#f3f7fa",
    borderColor: "#173d61",
  },
};

const SuiviList = () => {
  const { canValidate } = useSmqPermissions();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [processusFilter, setProcessusFilter] = useState("");
  const [criticiteFilter, setCriticiteFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [recapTab, setRecapTab] = useState(0);
  const [originTargetId, setOriginTargetId] = useState(null);
  const [originDetails, setOriginDetails] = useState({});
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ color: "#2e7d32", destination: null, messageInfo: "", open: false });
  const detailRequestRef = useRef(0);
  const originRequestRef = useRef(0);
  const dateRef = useRef(null);
  const indicatorRef = useRef(null);
  const resultRef = useRef(null);

  const loadCandidates = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const response = await SuiviService.getAll();
      if (!Array.isArray(response?.data)) throw new Error("Le service a retourné une liste invalide.");
      const rowsById = new Map(
        response.data.map((item) => [String(item.constat_id), item])
      );
      setCandidates(response.data.map((item) => ({
        ...item,
        parent_reference: item.parent_reference
          || rowsById.get(String(item.parent_id))?.reference
          || null,
      })));
    } catch (error) {
      setCandidates([]);
      setListError(error?.message || "Impossible de charger les non-conformités.");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const loadDetail = useCallback(async (constatId) => {
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await SuiviService.getDetail(constatId);
      if (requestId !== detailRequestRef.current) return;
      const nextDetail = response?.data;
      if (!nextDetail?.constat_id) throw new Error("Le détail reçu est invalide.");
      setDetail(nextDetail);

      const latestConfirmed = (nextDetail.suivis || []).find((item) => item.state === "confirmed");
      if (nextDetail.status === "done") {
        setForm(formFromSuivi(latestConfirmed || nextDetail.suivis?.[0], nextDetail.default_indicateur_efficacite));
      } else if (nextDetail.editable_suivi) {
        setForm(formFromSuivi(nextDetail.editable_suivi, nextDetail.default_indicateur_efficacite));
      } else {
        setForm(emptyForm(nextDetail.default_indicateur_efficacite));
      }
      setErrors({});
    } catch (error) {
      if (requestId !== detailRequestRef.current) return;
      setDetail(null);
      setDetailError(error?.message || "Impossible de charger le détail du suivi.");
    } finally {
      if (requestId === detailRequestRef.current) setDetailLoading(false);
    }
  }, []);

  const loadOriginDetail = useCallback(async (constatId) => {
    if (!constatId) return;

    const cacheKey = String(constatId);
    if (originDetails[cacheKey]?.data || originDetails[cacheKey]?.loading) return;

    const requestGeneration = originRequestRef.current;
    setOriginDetails((current) => ({
      ...current,
      [cacheKey]: { data: null, error: null, loading: true },
    }));

    try {
      const response = await SuiviService.getDetail(constatId);
      if (requestGeneration !== originRequestRef.current) return;
      if (!response?.data?.constat_id) throw new Error("Le détail reçu est invalide.");
      setOriginDetails((current) => ({
        ...current,
        [cacheKey]: { data: response.data, error: null, loading: false },
      }));
    } catch (error) {
      if (requestGeneration !== originRequestRef.current) return;
      setOriginDetails((current) => ({
        ...current,
        [cacheKey]: {
          data: null,
          error: error?.message || "Impossible de charger le dossier d’origine.",
          loading: false,
        },
      }));
    }
  }, [originDetails]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else {
      detailRequestRef.current += 1;
      setDetail(null);
      setDetailError(null);
    }
  }, [loadDetail, selectedId]);

  useEffect(() => {
    originRequestRef.current += 1;
    setRecapTab(0);
    setOriginDetails({});
    setOriginTargetId(null);
  }, [selectedId]);

  const selectableCandidates = useMemo(() => {
    const uniqueCandidates = new Map();

    candidates.forEach((item) => {
      if (item.parent_id === null || (item.parent_id !== null && item.status === "open")) {
        uniqueCandidates.set(String(item.constat_id), item);
      }
    });

    return Array.from(uniqueCandidates.values());
  }, [candidates]);

  const processusOptions = useMemo(
    () => Array.from(new Set(selectableCandidates.map((item) => item.processus).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr")),
    [selectableCandidates]
  );

  const filteredCandidates = useMemo(() => {
    const search = normalize(searchTerm);
    return selectableCandidates.filter(
      (item) =>
        (!search ||
          normalize(
            `${item.reference} ${item.description_constat} ${item.processus} ${item.direction_code}`
          ).includes(search)) &&
        (!processusFilter || item.processus === processusFilter) &&
        (!criticiteFilter || item.criticite === criticiteFilter) &&
        (!statusFilter || item.status === statusFilter)
    );
  }, [criticiteFilter, processusFilter, searchTerm, selectableCandidates, statusFilter]);

  useEffect(() => {
    if (selectedId && !filteredCandidates.some((item) => String(item.constat_id) === String(selectedId))) {
      setSelectedId(null);
    }
  }, [filteredCandidates, selectedId]);

  const selectedCandidate = selectableCandidates.find(
    (item) => String(item.constat_id) === String(selectedId)
  ) || null;
  const originCandidates = useMemo(() => {
    if (!selectedCandidate?.parent_id) return [];

    const parent = candidates.find(
      (item) => String(item.constat_id) === String(selectedCandidate.parent_id)
    ) || {
      constat_id: selectedCandidate.parent_id,
      reference: selectedCandidate.parent_reference || "NC parente",
    };
    const siblings = candidates.filter(
      (item) =>
        String(item.parent_id) === String(selectedCandidate.parent_id)
        && String(item.constat_id) !== String(selectedCandidate.constat_id)
    );

    return [parent, ...siblings];
  }, [candidates, selectedCandidate]);
  const originDetailState = originTargetId ? originDetails[String(originTargetId)] : null;
  const locked = !canValidate || detail?.status === "done";
  const currentUserName = detail?.current_user?.name || "—";

  const selectOriginTarget = (constatId) => {
    setOriginTargetId(constatId);
    loadOriginDetail(constatId);
  };

  const changeRecapTab = (_, nextTab) => {
    setRecapTab(nextTab);
    if (nextTab === 1) {
      const parentId = originCandidates[0]?.constat_id;
      const nextTargetId = originTargetId || parentId;
      if (nextTargetId) selectOriginTarget(nextTargetId);
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.date_verification) nextErrors.date_verification = "La date est obligatoire.";
    if (form.state !== "draft") {
      if (!form.indicateur_efficacite.trim()) nextErrors.indicateur_efficacite = "L’indicateur est obligatoire.";
      if (!form.resultat_verification.trim()) nextErrors.resultat_verification = "Le résultat est obligatoire.";
    }
    setErrors(nextErrors);
    const firstKey = ["date_verification", "indicateur_efficacite", "resultat_verification"].find((key) => nextErrors[key]);
    if (firstKey === "date_verification") dateRef.current?.focus();
    if (firstKey === "indicateur_efficacite") indicatorRef.current?.focus();
    if (firstKey === "resultat_verification") resultRef.current?.focus();
    return !firstKey;
  };

  const save = async () => {
    if (!canValidate || !detail || locked || saving || !validate()) return;
    setSaving(true);
    try {
      const payload = {
        commentaire: form.commentaire.trim() || null,
        constat_id: detail.constat_id,
        date_verification: form.date_verification,
        indicateur_efficacite: form.indicateur_efficacite.trim(),
        resultat_verification: form.resultat_verification.trim(),
        state: form.state,
        valeur_indicateur: form.valeur_indicateur.trim(),
      };
      const response = detail.editable_suivi?.suivi_id
        ? await SuiviService.update(detail.editable_suivi.suivi_id, payload)
        : await SuiviService.create(payload);
      const reopenedConstat = response?.data?.reopened_constat;
      if (form.state === "rejected" && !reopenedConstat?.constat_id) {
        throw new Error("Le sous-NC créé n’a pas été retourné par le service.");
      }
      setMessage({
        color: "#2e7d32",
        destination: reopenedConstat?.constat_id
          ? `/smq_nc/add?constat_id=${reopenedConstat.constat_id}&step=constat`
          : null,
        messageInfo: response?.message || "Suivi enregistré avec succès.",
        open: true,
      });
      await loadCandidates();
      if (!reopenedConstat) await loadDetail(detail.constat_id);
    } catch (error) {
      setMessage({ color: "#d32f2f", destination: null, messageInfo: error?.message || "Impossible d’enregistrer le suivi.", open: true });
    } finally {
      setSaving(false);
    }
  };

  const detailStatusLabel = detail?.status === "done" && detail?.has_reopened_child
    ? "Clôturée après réouverture"
    : STATUS_CONFIG[detail?.status]?.label || detail?.status;

  const closeMessage = () => {
    const destination = message.destination;
    setMessage((current) => ({ ...current, destination: null, open: false }));
    if (destination) navigate(destination);
  };

  return (
    <SMQMainContainer>
      <>
        <Message
          color={message.color}
          handleClose={closeMessage}
          messageInfo={message.messageInfo}
          open={message.open}
        />
        <HeaderRegistre
          icon={<AssessmentOutlinedIcon sx={{ color: "#56c596", fontSize: 16 }} />}
          title="Suivi de l’efficacité des actions"
        />

        <Box sx={{ backgroundColor: "#f1f5fb", minHeight: "calc(100vh - 56px)", px: { xs: 1.5, sm: 2.5 }, pb: 3 }}>

          {listError && (
            <Alert action={<Button color="inherit" onClick={loadCandidates}>Réessayer</Button>} severity="error" sx={{ mt: 1.5 }}>
              {listError}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #cbd8e6",
              borderRadius: "8px",
              mt: 1.5,
              p: 1.25,
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                justifyContent: "space-between",
                mb: filtersVisible ? 1.5 : 0,
              }}
            >
              {filtersVisible && (
                <Typography color="#60758a" fontSize={11} whiteSpace="nowrap">
                  Vue d'ensemble étapes 1→6 + vérification de l'efficacité
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  justifyContent: "flex-end",
                  ml: { sm: "auto" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Button
                  color="inherit"
                  onClick={() => setFiltersVisible((visible) => !visible)}
                  size="small"
                  startIcon={
                    filtersVisible ? (
                      <FilterAltOffOutlinedIcon />
                    ) : (
                      <FilterAltOutlinedIcon />
                    )
                  }
                  sx={filterButtonSx}
                  variant="outlined"
                >
                  {filtersVisible ? "Masquer filtres" : "Afficher filtres"}
                </Button>
              </Box>
            </Box>

            {filtersVisible && (
              <>
                <Typography
                  color="#52677c"
                  fontSize={10}
                  fontWeight={700}
                  letterSpacing="0.05em"
                  mb={1}
                >
                  FILTRES
                </Typography>
                <Grid alignItems="flex-start" container spacing={1.25}>
                  <Grid item md={6} sm={6} xs={12}>
                    <TextField
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#62a5bd", fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                      label="Recherche"
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Référence, constat ou processus..."
                      size="small"
                      sx={filterFieldSx}
                      value={searchTerm}
                    />
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      disabled={listLoading || selectableCandidates.length === 0}
                      label="Processus"
                      onChange={(event) => setProcessusFilter(event.target.value)}
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={processusFilter}
                    >
                      <MenuItem value="">Tous les processus</MenuItem>
                      {processusOptions.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                      {!listLoading && processusOptions.length === 0 && (
                        <MenuItem disabled value="__empty__">
                          Aucun processus disponible
                        </MenuItem>
                      )}
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Criticité"
                      onChange={(event) => setCriticiteFilter(event.target.value)}
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={criticiteFilter}
                    >
                      <MenuItem value="">Toutes les criticités</MenuItem>
                      <MenuItem value="mineure">Mineure</MenuItem>
                      <MenuItem value="majeure">Majeure</MenuItem>
                      <MenuItem value="critique">Critique</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextField
                      label="Statut"
                      onChange={(event) => setStatusFilter(event.target.value)}
                      select
                      size="small"
                      sx={filterFieldSx}
                      value={statusFilter}
                    >
                      <MenuItem value="">Tous les statuts</MenuItem>
                      <MenuItem value="open">Ouverte</MenuItem>
                      <MenuItem value="in_progress">En cours</MenuItem>
                      <MenuItem value="reopen">Réouverte</MenuItem>
                      <MenuItem value="done">Clôturée</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>

          <Paper elevation={0} sx={{ border: "1px solid #cbd8e6", borderRadius: "10px", mt: 1.5, p: { xs: 1.5, sm: 2 } }} variant="outlined">
            <Typography color="#10263d" fontSize={13} fontWeight={800} sx={{ mb: 1 }}>
              Sélectionner une NC à évaluer ({filteredCandidates.length} NC)
            </Typography>
            <Autocomplete
              disabled={listLoading || selectableCandidates.length === 0}
              fullWidth
              getOptionLabel={candidateLabel}
              isOptionEqualToValue={(option, value) => String(option.constat_id) === String(value.constat_id)}
              loading={listLoading}
              noOptionsText={selectableCandidates.length === 0
                ? "Aucune NC active disponible"
                : "Aucune NC correspondant aux filtres"}
              onChange={(_, value) => setSelectedId(value?.constat_id || null)}
              options={filteredCandidates}
              renderInput={(params) => <TextField {...params} placeholder="— Choisir une non-conformité —" size="small" />}
              value={selectedCandidate}
            />

            {!selectedId && !listLoading && selectableCandidates.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Aucune non-conformité active n&apos;est actuellement disponible.
              </Alert>
            )}

            {!selectedId && !listLoading && selectableCandidates.length > 0 && (
              <Box sx={{ alignItems: "center", backgroundColor: "#f7f9fc", border: "1px dashed #b8c9da", borderRadius: "9px", display: "flex", flexDirection: "column", justifyContent: "center", minHeight: 190, mt: 2, p: 3, textAlign: "center" }}>
                <AssessmentOutlinedIcon sx={{ color: "#8ba1b6", fontSize: 42, mb: 1 }} />
                <Typography color="#10263d" fontWeight={800}>Aucune NC sélectionnée</Typography>
                <Typography color="#60758a" fontSize={12}>Choisissez une NC pour afficher le récapitulatif et vérifier l’efficacité de ses actions.</Typography>
              </Box>
            )}

            {detailLoading && <Box sx={{ mt: 2 }}><Skeleton height={95} /><Skeleton height={130} /><Skeleton height={130} /></Box>}
            {detailError && !detailLoading && (
              <Alert action={<Button color="inherit" onClick={() => loadDetail(selectedId)}>Réessayer</Button>} severity="error" sx={{ mt: 2 }}>{detailError}</Alert>
            )}

            {detail && !detailLoading && (
              <Box sx={{ mt: 2 }}>
                {selectedCandidate?.parent_id && (
                  <Tabs
                    onChange={changeRecapTab}
                    sx={{ borderBottom: "1px solid #cbd8e6", mb: 1.5, minHeight: 38 }}
                    value={recapTab}
                  >
                    <Tab label="NC suivie" sx={{ fontSize: 11, fontWeight: 800, minHeight: 38, textTransform: "none" }} />
                    <Tab label="Voir origine" sx={{ fontSize: 11, fontWeight: 800, minHeight: 38, textTransform: "none" }} />
                  </Tabs>
                )}

                {recapTab === 0 && (
                  <>
                    <Box sx={{ alignItems: "center", display: "flex", gap: 1, mb: 1 }}>
                      <Box sx={{ backgroundColor: "#10263d", borderRadius: "5px", color: "#fff", fontSize: 10, fontWeight: 800, px: 1.2, py: 0.55 }}>RÉCAPITULATIF</Box>
                      <Typography color="#10263d" fontSize={11} fontWeight={800}>ÉTAPES 1 À 6</Typography>
                      <Box sx={{ color: STATUS_CONFIG[detail.status]?.color || "#60758a", fontSize: 11, fontWeight: 800, ml: "auto" }}>{detailStatusLabel}</Box>
                    </Box>

                    <RecapSteps detail={detail} />

                    <Box sx={{ borderTop: "2px dashed #c7d6e5", mt: 2.5, pt: 2.2 }}>
                  <Box sx={{ backgroundColor: "#10263d", borderRadius: "5px", color: "#fff", display: "inline-block", fontSize: 10, fontWeight: 800, mb: 1.5, px: 1.2, py: 0.55 }}>ÉTAPE 6 — SUIVI EFFICACITÉ</Box>
                  {locked ? (
                    <Alert severity="success" sx={{ mb: 2 }}>Cette NC est clôturée — consultation uniquement.</Alert>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>La NC ne peut être clôturée que si l’indicateur confirme l’efficacité de l’action corrective. Sinon, rouvrez la NC et relancez l’analyse.</Alert>
                  )}

                  <Grid container spacing={1.5}>
                    <Grid item xs={12}><TextField disabled fullWidth label="Référence NC" size="small" value={detail.reference} /></Grid>
                    <Grid item xs={12} md={6}><TextField disabled={locked || saving} error={Boolean(errors.date_verification)} fullWidth helperText={errors.date_verification} inputRef={dateRef} InputLabelProps={{ shrink: true }} label="Date de vérification *" onChange={(event) => setForm((current) => ({ ...current, date_verification: event.target.value }))} size="small" type="date" value={form.date_verification} /></Grid>
                    <Grid item xs={12} md={6}><TextField disabled fullWidth label="Vérifié par" size="small" value={locked ? displayValue((detail.suivis || []).find((item) => item.state === "confirmed")?.evaluateur_name) : currentUserName} /></Grid>
                    <Grid item xs={12}>
                      <Typography color="#536b91" fontSize={10} fontWeight={800} sx={{ mb: 0.5 }}>INDICATEUR D’EFFICACITÉ</Typography>
                      <Box component="textarea" disabled={locked || saving} maxLength={255} onChange={(event) => setForm((current) => ({ ...current, indicateur_efficacite: event.target.value }))} ref={indicatorRef} sx={textareaSx(Boolean(errors.indicateur_efficacite))} value={form.indicateur_efficacite} />
                      {errors.indicateur_efficacite && <Typography color="error" fontSize={11}>{errors.indicateur_efficacite}</Typography>}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography color="#536b91" fontSize={10} fontWeight={800} sx={{ mb: 0.5 }}>RÉSULTAT DE LA VÉRIFICATION</Typography>
                      <Box component="textarea" disabled={locked || saving} onChange={(event) => setForm((current) => ({ ...current, resultat_verification: event.target.value }))} ref={resultRef} sx={{ ...textareaSx(Boolean(errors.resultat_verification)), minHeight: 80 }} value={form.resultat_verification} />
                      {errors.resultat_verification && <Typography color="error" fontSize={11}>{errors.resultat_verification}</Typography>}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography color="#536b91" fontSize={10} fontWeight={800} sx={{ mb: 0.5 }}>COMMENTAIRE</Typography>
                      <Box component="textarea" disabled={locked || saving} onChange={(event) => setForm((current) => ({ ...current, commentaire: event.target.value }))} sx={textareaSx()} value={form.commentaire} />
                    </Grid>
                  </Grid>

                  <Typography color="#536b91" fontSize={10} fontWeight={800} sx={{ mb: 0.7, mt: 1.5 }}>STATUT DE CLÔTURE</Typography>
                  <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                    <Button disabled={locked || saving} onClick={() => setForm((current) => ({ ...current, state: "confirmed" }))} startIcon={<CheckCircleOutlineIcon />} sx={closureStatusButtonSx(form.state === "confirmed")} variant="outlined">NC clôturée — efficacité confirmée</Button>
                    <Button disabled={locked || saving} onClick={() => setForm((current) => ({ ...current, state: "rejected" }))} startIcon={<ReplayIcon />} sx={closureStatusButtonSx(form.state === "rejected")} variant="outlined">NC Réouverte — action insuffisante</Button>
                    <Button disabled={locked || saving} onClick={() => setForm((current) => ({ ...current, state: "draft" }))} startIcon={<HourglassEmptyIcon />} sx={closureStatusButtonSx(form.state === "draft")} variant="outlined">En attente de vérification</Button>
                  </Box>

                  {detail.status !== "done" && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                      <Button color="success" disabled={locked || saving} onClick={save} startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <AssessmentOutlinedIcon />} variant="contained">Enregistrer le suivi</Button>
                    </Box>
                  )}
                    </Box>
                  </>
                )}

                {recapTab === 1 && selectedCandidate?.parent_id && (
                  <Box>
                    <Tabs
                      onChange={(_, value) => selectOriginTarget(value)}
                      scrollButtons="auto"
                      sx={{ backgroundColor: "#f4f7fb", borderRadius: "7px", mb: 1.5, minHeight: 36 }}
                      value={originTargetId || originCandidates[0]?.constat_id || false}
                      variant="scrollable"
                    >
                      {originCandidates.map((candidate, index) => (
                        <Tab
                          key={candidate.constat_id}
                          label={index === 0 ? `NC parent — ${candidate.reference}` : `Sous-NC — ${candidate.reference}`}
                          sx={{ fontSize: 10, fontWeight: 800, minHeight: 36, textTransform: "none" }}
                          value={candidate.constat_id}
                        />
                      ))}
                    </Tabs>

                    {originDetailState?.loading && <Box><Skeleton height={95} /><Skeleton height={130} /><Skeleton height={130} /></Box>}
                    {originDetailState?.error && !originDetailState.loading && (
                      <Alert
                        action={<Button color="inherit" onClick={() => loadOriginDetail(originTargetId)}>Réessayer</Button>}
                        severity="error"
                      >
                        {originDetailState.error}
                      </Alert>
                    )}
                    {originDetailState?.data && !originDetailState.loading && (
                      <>
                        <Box sx={{ alignItems: "center", display: "flex", gap: 1, mb: 1 }}>
                          <Box sx={{ backgroundColor: "#10263d", borderRadius: "5px", color: "#fff", fontSize: 10, fontWeight: 800, px: 1.2, py: 0.55 }}>ORIGINE</Box>
                          <Typography color="#10263d" fontSize={11} fontWeight={800}>{originDetailState.data.reference}</Typography>
                          <Box sx={{ color: STATUS_CONFIG[originDetailState.data.status]?.color || "#60758a", fontSize: 11, fontWeight: 800, ml: "auto" }}>
                            {STATUS_CONFIG[originDetailState.data.status]?.label || originDetailState.data.status}
                          </Box>
                        </Box>
                        <RecapSteps detail={originDetailState.data} />
                        <ReadOnlySuiviHistory suivis={originDetailState.data.suivis} />
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </>
    </SMQMainContainer>
  );
};

export default SuiviList;
